import { useState, type CSSProperties, type ReactNode } from 'react'
import Icon from '../../docs/Icon'
import Switch from '../ui/Switch'
import RangeSlider from '../ui/RangeSlider'
import DateRangePicker from '../ui/DateRangePicker'

// Live examples for the textarea, switch, range slider and date range picker.
// A mini specimen is a still preview for the catalog: no ids, nothing focusable.

type SpecimenProps = { mini?: boolean }

function Still({ children }: { children: ReactNode }) {
  return <div className="lib-framed lib-still">{children}</div>
}

function TextareaSpecimen({ mini }: SpecimenProps) {
  const [text, setText] = useState('')
  const [touched, setTouched] = useState(false)
  if (mini)
    return (
      <Still>
        <span className="mds-label">What happened?</span>
        <span className="mds-hint">Include the date and the office.</span>
        <span className="mds-textarea lib-fake-input lib-fake-textarea">On October 14 the passport office…</span>
      </Still>
    )
  const invalid = touched && text.trim() === ''
  return (
    <div className="mds-field">
      <label className="mds-label" htmlFor="spec-textarea">
        What happened?
      </label>
      <span className="mds-hint" id="spec-textarea-hint">
        Include the date and the office. Leave the field empty and move on to see the error.
      </span>
      <textarea
        id="spec-textarea"
        className="mds-textarea"
        rows={5}
        value={text}
        aria-invalid={invalid}
        aria-describedby={`spec-textarea-hint${invalid ? ' spec-textarea-error' : ''}`}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => setTouched(true)}
      />
      {invalid && (
        <span className="mds-error-text" id="spec-textarea-error">
          Describe what happened.
        </span>
      )}
    </div>
  )
}

function SwitchSpecimen({ mini }: SpecimenProps) {
  const [reminders, setReminders] = useState(true)
  if (mini)
    return (
      <Still>
        <label className="mds-switch">
          <input type="checkbox" role="switch" className="mds-switch__input" defaultChecked tabIndex={-1} />
          <span className="mds-switch__track" aria-hidden="true">
            <span className="mds-switch__thumb" />
          </span>
          <span className="mds-switch__label">Email reminders</span>
        </label>
      </Still>
    )
  return (
    <div className="lib-stack">
      <Switch
        label="Email reminders"
        hint="A reminder three days before each appointment."
        checked={reminders}
        onChange={setReminders}
      />
      <p className="mds-muted" aria-live="polite">
        {reminders ? 'Reminders go to the email on your account.' : 'You will not get reminders.'}
      </p>
      <Switch label="Text message reminders" hint="Add a mobile number to your profile first." disabled />
    </div>
  )
}

const miles = (value: number) => `${value} ${value === 1 ? 'mile' : 'miles'}`

function RangeSliderSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-label">Search radius</span>
        <span className="mds-range__value">25 miles</span>
        <span className="mds-range__row">
          <span className="mds-range__end">5</span>
          <input type="range" className="mds-range" min={5} max={100} defaultValue={25} tabIndex={-1} aria-label="Search radius" style={{ '--mds-range-fill': '21%' } as CSSProperties} />
          <span className="mds-range__end">100</span>
        </span>
      </Still>
    )
  return <RangeSlider label="Search radius" hint="Offices within this distance of your ZIP code." min={5} max={100} step={5} defaultValue={25} format={miles} />
}

function DateRangePickerSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-label">Travel dates</span>
        <span className="mds-date-range__fields">
          <span className="mds-input lib-fake-input">
            11/02/2026 <Icon name="calendar" size={16} />
          </span>
          <span className="mds-input lib-fake-input">
            11/09/2026 <Icon name="calendar" size={16} />
          </span>
        </span>
      </Still>
    )
  return <DateRangePicker legend="Travel dates" hint="The first and last day you are away." startLabel="Departure date" endLabel="Return date" />
}

export const formsSpecimens: Record<string, (props: SpecimenProps) => ReactNode> = {
  textarea: TextareaSpecimen,
  switch: SwitchSpecimen,
  'range-slider': RangeSliderSpecimen,
  'date-range-picker': DateRangePickerSpecimen,
}
